import { useEffect, type ReactNode } from 'react'
import { X, Sparkles, CheckCircle2, ShieldAlert, Eye } from 'lucide-react'
import { formatKickoff, formatOdds, liveScoreLabel, probabilityDisplay, resolvePickBadgeStatus } from '@/lib/utils'
import { StatusBadge } from './StatusBadge'
import { TeamCrest } from './TeamCrest'
import { ResponsibleNotice } from './ResponsibleNotice'
import { usePickExplanation } from '@/hooks/usePicks'
import type { PickLeg } from '@/types/miniApp'

const NO_DATA_DEFAULT = 'No disponemos de información suficiente para valorar este apartado.'

/** Sprint "Product Experience V2" (05/09/2026, Sección 3 del encargo): cada una de las 3
 * secciones del análisis (Por qué le gusta a JA / Qué puede fallar / Qué hay que vigilar)
 * SIEMPRE se muestra -- nunca desaparece cuando no hay señales reales, para que el usuario
 * entienda "John Anthony evaluó esto y no encontró nada que destacar aquí" en vez de leer
 * la ausencia de la sección como un hueco del producto. `reasons` vacío/ausente cae en un
 * mensaje honesto propio de cada sección -- nunca se inventa una razón. */
function ReasonList({ reasons, emptyText = NO_DATA_DEFAULT }: { reasons?: string[]; emptyText?: string }) {
  if (!reasons || reasons.length === 0) {
    return <p className="text-xs text-text-dim italic leading-relaxed">{emptyText}</p>
  }
  return (
    <ul className="space-y-1.5">
      {reasons.map((r, i) => (
        <li key={i} className="text-xs text-text-muted leading-relaxed flex items-start gap-2">
          <span className="text-brand mt-1 shrink-0">•</span>
          <span>{r}</span>
        </li>
      ))}
    </ul>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border/20 last:border-0">
      <span className="text-xs text-text-dim font-medium">{label}</span>
      <span className="text-xs font-bold text-text tabular-nums">{value}</span>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">{title}</p>
      {children}
    </div>
  )
}

export function PickDetailSheet({ pick, onClose }: { pick: PickLeg; onClose: () => void }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // P0.26 (Sección 2/24 del encargo): explicación comercial bajo demanda -- solo se pide al
  // backend cuando este drawer está realmente abierto, nunca en el listado masivo.
  const { data: explanationData, isLoading: explanationLoading } = usePickExplanation(pick.decision_record_id)

  const homeName = pick.home_team ?? 'Local'
  const awayName = pick.away_team ?? 'Visitante'
  const selectionLabel = (pick.selection_label ?? pick.selection ?? '').toString().toUpperCase()
  const marketLabel = pick.market_label ?? pick.market ?? 'Mercado Principal'
  // P0.23 (22/08/2026, Sección 5A/19 del encargo): bug real encontrado -- cuando
  // `model_probability` no venía (null/0/undefined), se mostraba un "65.0%" fijo inventado
  // en vez de N/D ("no mostrar NaN/undefined... no convertir NULL en 0 ni fabricar un
  // valor"). Ver `probabilityDisplay` (`lib/utils.ts`) -- `kind: 'unknown'` cuando no hay
  // dato, cada consumidor decide su propio "N/D" en vez de heredar un número falso.
  // P0.32 (26/08/2026, rediseño Mini App -- Sección 1 del encargo): BUG REAL corregido --
  // el modal seguía mostrando "Probabilidad estimada 85.0%" para picks `conservative_
  // fallback`/`experimental`, presentando el tope de seguridad genérico de `DecisionEngine`
  // (nunca una probabilidad calibrada específica de ESTE pick) como si lo fuera. Ver
  // `probabilityDisplay` (`lib/utils.ts`) -- nunca sustituye el número por otro inventado, en
  // fallback se muestra confianza + aviso, nunca un porcentaje.
  const probDisplay = probabilityDisplay(pick.model_probability, pick.probability_is_calibrated)
  // "Cuota justa" = 1/probabilidad del modelo -- derivación puramente de PRESENTACIÓN, MISMA
  // fórmula que ya usa el backend en varios puntos (p.ej. `historical_decision_runner.py`,
  // `fair_odds=(1.0/model_probability) if model_probability>0 else None`), nunca inventada
  // aquí. `_build_leg_detail` no expone un campo `fair_odds` propio para Picks/Parlays, así
  // que se recalcula del mismo dato ya presente (`model_probability`) en vez de pedir una
  // extensión backend para algo que ya es derivable sin ambigüedad (Sección 26).
  const fairOdds = pick.model_probability != null && pick.model_probability > 0 ? 1 / pick.model_probability : null
  // P1.10 (25/09/2026, Secciones 1/2 del encargo): MISMA lectura canónica que las cards --
  // nunca un segundo cómputo local de estado.
  const badgeStatus = resolvePickBadgeStatus(pick.fixture_status, pick.settled_status)
  const isLive = pick.fixture_status === 'live'
  const liveScore = isLive ? liveScoreLabel(pick.home_score, pick.away_score) : null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in-up" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-panel rounded-t-3xl border-t border-border max-h-[85vh] overflow-y-auto animate-fade-in-up shadow-2xl">
        
        {/* Header Modal */}
        <div className="sticky top-0 bg-panel border-b border-border px-5 py-3.5 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-400" />
            <p className="text-xs font-black uppercase tracking-wider text-text">Análisis de Inteligencia IA</p>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="p-1.5 rounded-xl bg-panel-elevated text-text-dim hover:text-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Enfrentamiento con Escudos Grandes (48px) */}
          <div className="bg-gradient-to-b from-panel-elevated/80 to-panel p-4 rounded-2xl border border-border/60 text-center space-y-3">
            <div className="flex items-center justify-between text-2xs text-text-dim font-bold">
              <span>{pick.league ?? 'Competición Oficial'}</span>
              <span className="font-mono">{formatKickoff(pick.kickoff_utc, { withYear: false })}</span>
            </div>
            {isLive && liveScore && (
              <div className="flex items-center justify-center gap-2">
                <StatusBadge status="en_juego" />
                <span className="text-lg font-mono font-black text-brand">{liveScore}</span>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 py-1">
              <div className="flex flex-col items-center gap-2 w-24">
                <TeamCrest src={pick.home_team_logo} alt={homeName} size={48} className="shadow-md" />
                <span className="text-xs font-black text-text line-clamp-2">{homeName}</span>
              </div>

              <span className="text-lg font-mono font-black text-text-dim">VS</span>

              <div className="flex flex-col items-center gap-2 w-24">
                <TeamCrest src={pick.away_team_logo} alt={awayName} size={48} className="shadow-md" />
                <span className="text-xs font-black text-text line-clamp-2">{awayName}</span>
              </div>
            </div>
          </div>

          {/* Selección + Cuota de referencia (JA) -- Sección 3 del encargo: "cuota JA/
              referencia" junto a probabilidad, siempre visibles. La cuota justa (1/prob.
              del modelo, MISMA fórmula que ya usa el backend en varios puntos) se muestra
              como referencia secundaria de John Anthony junto a la cuota real de mercado --
              nunca sustituye a la real, nunca se muestra si no hay probabilidad calibrada. */}
          <div className="bg-emerald-950/40 border border-emerald-500/40 p-4 rounded-2xl space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block">SELECCIÓN RECOMENDADA</span>
                <h3 className="text-base font-black text-text mt-0.5">{selectionLabel}</h3>
                <p className="text-2xs font-bold text-text-dim">{marketLabel}</p>
              </div>
              <div className="text-right shrink-0">
                <span className="text-[9px] font-bold text-text-dim uppercase block">CUOTA</span>
                <span className="text-2xl font-black text-emerald-400 font-mono">@{formatOdds(pick.odds)}</span>
              </div>
            </div>
            {/* P1.10 (25/09/2026, Sección 5 del encargo -- Odds Truth visible): SOLO datos
                reales ya persistidos (`MatchOdds.bookmaker`/`DecisionRecord.odds_captured_at`)
                -- nunca inferidos ni fabricados. Fallback honesto "N/D" cuando falte alguno,
                nunca se omite la fila en silencio (para no insinuar que el dato "no existe
                como concepto" en vez de "no se capturó esta vez").
                Sección E (25/09/2026, "Odds Truth" -- BUG REAL corregido): esta fila emparejaba
                el bookmaker EN VIVO (`pick.bookmaker`, prioritario en el `??` anterior) con
                "Cuota capturada" (`odds_captured_at`), un timestamp que en realidad pertenece a
                `reference_bookmaker` -- la misma cuota congelada mostrada arriba como "@1.92"
                (canario real: bet365 aparecía como si fuera de BETANO). Ahora la fila prioriza
                `reference_bookmaker` (a quien SÍ pertenecen la cuota y el timestamp); el
                bookmaker en vivo, si es distinto, se muestra aparte y rotulado como actual. */}
            <div className="hairline pt-3 flex items-center justify-between gap-3 text-2xs">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Bookmaker (cuota mostrada)</p>
                <p className="text-xs font-bold text-text mt-0.5">{pick.reference_bookmaker ?? pick.bookmaker ?? 'N/D'}</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Cuota capturada</p>
                <p className="text-xs font-bold text-text mt-0.5">
                  {pick.odds_captured_at ? formatKickoff(pick.odds_captured_at, { withYear: false }) : 'N/D'}
                </p>
              </div>
            </div>
            {pick.bookmaker && pick.reference_bookmaker && pick.bookmaker !== pick.reference_bookmaker && (
              <p className="text-[9px] text-text-dim">
                Actualmente disponible en {pick.bookmaker} (precio no verificado por JA)
              </p>
            )}
            <div className="hairline pt-3 flex items-center justify-between gap-3">
              <div>
                {/* Sprint "Corrección Final Product Experience V2" (10/09/2026, Sección 4
                    del encargo): "Probabilidad JA no disponible" -- nunca un "N/D" seco,
                    nunca sustituida por Confianza (dato distinto: solidez de la evidencia,
                    no estimación de ocurrencia).
                    Sprint "Corrección Puntual -- Labs + Probabilidad Cliente" (10/09/2026,
                    Sección 3 del encargo): BUG REAL corregido -- esta condición solo trataba
                    `kind === 'calibrated'` como "hay número", así que un `fallback` real (valor
                    no-nulo, efectivamente usado por JA para recomendar, solo sin calibración
                    histórica específica de este mercado) caía en el mismo "No disponible" que
                    un `unknown` genuino (`value == null`). "No disponible" ahora se reserva
                    EXCLUSIVAMENTE para `unknown` -- `fallback` muestra su `label` real (0
                    decimales, para no aparentar precisión calibrada) con un aviso honesto
                    aparte, nunca inventando un número ni ocultando uno real. */}
                <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Probabilidad JA</p>
                <p className="text-sm font-mono font-black text-cyan mt-0.5">
                  {probDisplay.kind === 'unknown' ? 'No disponible' : probDisplay.label}
                </p>
                {probDisplay.kind === 'fallback' && (
                  <p className="text-[8px] text-text-dim font-normal mt-0.5">Sin calibración histórica específica</p>
                )}
              </div>
              {fairOdds != null && (
                <div className="text-right">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Cuota de referencia (JA)</p>
                  <p className="text-sm font-mono font-black text-cyan mt-0.5">@{fairOdds.toFixed(2)}</p>
                </div>
              )}
              {pick.confidence_score != null && (
                <div className="text-right">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Confianza</p>
                  {/* Sección 4 del encargo: "no necesita porcentaje técnico" -- solo
                      ALTA/MEDIA/BAJA, nunca el número que se confunde con Probabilidad. */}
                  <p className="text-sm font-mono font-black text-brand mt-0.5">
                    {pick.confidence_score >= 75 ? 'Alta' : pick.confidence_score >= 50 ? 'Media' : 'Baja'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sprint "Product Experience V2" (05/09/2026, Sección 3 del encargo): las 3
              preguntas que el usuario debe poder responder tras leer el análisis --
              reemplaza la grilla de métricas técnicas (Risk Score/Pick Quality Score/EV como
              números sueltos, Sección 3: "elimina lenguaje interno/técnico que no ayude al
              cliente") por 3 secciones SIEMPRE visibles, cada una con su propia fuente REAL
              de datos ya calculada por el backend -- nunca una frase inventada aquí, y nunca
              una sección que desaparece en silencio cuando no hay señales (ver `ReasonList`,
              siempre honesta ante datos vacíos). */}

          {/* A) Por qué le gusta a JA -- `HumanExplanationEngine.explain()`, MISMA fuente que
              ya usaba este sheet (P0.26), solo renombrada a la pregunta real del usuario. */}
          <Section title="Por qué le gusta a JA">
            <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl space-y-4">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-xs text-text-muted leading-relaxed">
                  {/* Sprint "Corrección Puntual -- Labs + Probabilidad Cliente" (10/09/2026,
                      Sección 4 del encargo): BUG REAL corregido -- la rama `fallback` mostraba
                      "John Anthony tiene confianza moderada... sin una probabilidad calibrada
                      específica todavía para este mercado", meta-lenguaje sobre CALIBRACIÓN
                      (jerga interna del motor), nunca una razón futbolística -- y encima
                      sustituía Probabilidad por Confianza en la misma frase (Sección 3: nunca
                      intercambiarlas). `probDisplay.label` ya es un valor real en ambos casos
                      no-`unknown` (ver `probabilityDisplay`, `lib/utils.ts`), así que ambos
                      comparten la misma frase honesta -- nunca se inventa una razón. */}
                  {probDisplay.kind !== 'unknown' ? (
                    <>El modelo estima un <strong className="text-cyan">{probDisplay.label} de probabilidad</strong> para esta selección.</>
                  ) : (
                    <>No disponemos de una probabilidad estimada del modelo para esta selección.</>
                  )}
                  {pick.market_explanation && (
                    <span className="block mt-1 text-text-dim">{pick.market_explanation}</span>
                  )}
                </p>
              </div>

              {explanationLoading ? (
                <p className="text-xs text-text-dim italic">Cargando datos de respaldo…</p>
              ) : (
                <ReasonList reasons={explanationData?.human_explanation} />
              )}
            </div>
          </Section>

          {/* Sprint "Corrección Final Product Experience V2" (10/09/2026, Secciones 2/3 del
              encargo): `risk_reasons_display`/`pick_quality_reasons_display`/`confidence_
              reasons_display` (salida HUMANIZADA de `RiskEngine`/`PickQualityEngine`/
              `ConfidenceEngine`) siguen sin leerse aquí -- ninguno de sus factores es un
              hecho futbolístico, siguen intactos en `DecisionRecord` para Admin/Auditoría.
              Sprint "Corrección Puntual -- Qué puede fallar + A vigilar" (10/09/2026): B)/C)
              ahora SÍ reciben datos reales del backend (`HumanExplanationEngine.
              explain_risks()`/`explain_watch()`, misma fuente/consultas que A) -- el
              fallback honesto ("John Anthony no identifica..."/"Sin alertas importantes...")
              sigue siendo el ÚLTIMO recurso, solo cuando de verdad no existe ninguna señal
              contraria/pendiente real defendible, nunca porque el backend nunca lo intentó. */}
          <Section title="Qué puede fallar">
            <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl">
              <div className="flex items-start gap-2.5">
                <ShieldAlert className="h-4 w-4 text-warn shrink-0 mt-0.5" />
                {explanationLoading ? (
                  <p className="text-xs text-text-dim italic">Cargando datos de respaldo…</p>
                ) : (
                  <ReasonList
                    reasons={explanationData?.risk_explanation}
                    emptyText="John Anthony no identifica escenarios de riesgo concretos para esta selección."
                  />
                )}
              </div>
            </div>
          </Section>

          <Section title="A vigilar">
            <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl">
              <div className="flex items-start gap-2.5">
                <Eye className="h-4 w-4 text-cyan shrink-0 mt-0.5" />
                {explanationLoading ? (
                  <p className="text-xs text-text-dim italic">Cargando datos de respaldo…</p>
                ) : (
                  <ReasonList reasons={explanationData?.watch_explanation} emptyText="Sin alertas importantes antes del partido." />
                )}
              </div>
            </div>
          </Section>

          <ResponsibleNotice />

          {/* Estado del Evento */}
          <Section title="Estado del Evento">
            <div className="bg-panel-elevated/40 border border-border/40 p-3 rounded-2xl">
              {badgeStatus && (
                <div className="flex items-center justify-between py-1.5 border-b border-border/20">
                  <span className="text-xs text-text-dim font-medium">Estado</span>
                  <StatusBadge status={badgeStatus} />
                </div>
              )}
              {isLive && liveScore && <Row label="Marcador en vivo" value={liveScore} />}
              {pick.score_display && pick.score_display !== 'Pendiente' && (
                <Row label="Marcador final" value={pick.score_display} />
              )}
              {/* P0.26.1 (Sección D del encargo): resultado real de ESTE mercado (backend
                  ya lo calculaba, `market_result_display`, sin renderizarse aquí) + la
                  estadística final real cuando el mercado es córners/tarjetas/tiros/tiros a
                  puerta (`stat_result_display`, nuevo -- lee de MatchStatistics ya
                  persistido, nunca inventado). Ausente/null = sin dato final todavía. */}
              {pick.market_result_display && (
                <Row label="Resultado del mercado" value={pick.market_result_display} />
              )}
              {pick.stat_result_display && (
                <Row label="Estadística final" value={pick.stat_result_display} />
              )}
            </div>
          </Section>
        </div>
      </div>
    </div>
  )
}
